import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';

import {
  deleteQueryParam,
  filterAndSortColumns,
  filterTableData,
  findArrayElementCaseInsensitive,
  getUniqueTags,
} from '../../misc/functions';
import {
  DEFAULT_CURRENT_PAGE,
  DEFAULT_PER_PAGE,
  PER_PAGE_VALUES,
  WEBSITES_DATA_FILENAME,
} from '../../misc/misc.constants';
import { URL_PARAMETERS } from '../../misc/url.constants';
import { COLUMNS } from '../../misc/columns.constants';
import { fetchWebsitesData } from './table.api';
import type { RootState } from '../../store/store';
import type { Filters, SortState, Website, WebsitesData } from '../../types';

export const ASC = 'asc';
export const DESC = 'desc';

interface WebsitesDataPayload {
  websitesData: WebsitesData;
  websitesDataETag: string | null;
  websitesDataSource: 'file' | 'db';
  filters: Filters;
  allTags: string[];
  defaultShowColumns: string[];
  renderableColumns: string[];
  urlParams: UrlParamsPayload;
}

type RejectValue = { message: string };

export interface TableState {
  allTags: string[];
  checkboxes: number[];
  currentPage: number;
  defaultShowColumns: string[];
  filters: Filters;
  perPage: number;
  renderableColumns: string[];
  showColumns: string[];
  sort: SortState;
  tags: string[];
  websitesData: WebsitesData | null;
  websitesDataETag: string | null;
  websitesDataLoaded: boolean;
  websitesDataSource: string;
  websitesDataStatus: 'idle' | 'pending' | 'succeeded' | 'failed';
}

export interface UrlParamsPayload {
  currentPage?: number;
  perPage?: number;
  filters: Filters;
  showColumns: string[];
  sort: Partial<SortState>;
  tags: string[];
}

export const getWebsitesData = createAsyncThunk<
  WebsitesDataPayload,
  void,
  { state: RootState; rejectValue: RejectValue }
>(
  'table/getWebsitesData',
  async (_, { rejectWithValue }) => {
    const response = await fetchWebsitesData();

    if (response.status !== 200) {
      return rejectWithValue({ message: `Status code: ${response.status}` });
    }

    const websitesDataETag = response.headers.get('ETag');
    const websitesData = (await response.json()) as WebsitesData;
    const websitesDataSource = response.url.includes(WEBSITES_DATA_FILENAME)
      ? 'file'
      : 'db';

    const columns = websitesData.columns;
    const websites = websitesData.websites;

    if (!websitesData.env) {
      websitesData.env = 'demo';
    }

    const filters: Filters = {};
    columns.forEach((col) => {
      if (!col.renderFilter) return;
      if (col.name === COLUMNS.tags) return;
      filters[col.name] = '';
    });

    const allTags = getUniqueTags(websites).sort();
    const defaultShowColumns = columns
      .filter((col) => col.showColumn)
      .map((col) => col.name);
    const renderableColumns = columns
      .filter((col) => col.renderColumn)
      .map((col) => col.name);

    const urlParams = parseUrlParams({
      search: window.location.search,
      filters,
      renderableColumns,
      defaultShowColumns,
      websites,
    });

    return {
      websitesData,
      websitesDataETag,
      websitesDataSource,
      filters,
      allTags,
      defaultShowColumns,
      renderableColumns,
      urlParams,
    };
  },
  {
    // Skip duplicate requests while one is already in flight.
    condition: (_, { getState }) =>
      getState().table.websitesDataStatus !== 'pending',
  },
);

interface ParseUrlParamsInput {
  search: string;
  filters: Filters;
  renderableColumns: string[];
  defaultShowColumns: string[];
  websites: Website[];
}

// Reads filters, tags, sort, pagination and visible columns from the URL query
// string into a plain payload, without any store access. Running this inside
// getWebsitesData lets the table hydrate from a deep link in a single atomic
// transition instead of flashing default state first.
export function parseUrlParams({
  search,
  filters,
  renderableColumns,
  defaultShowColumns,
  websites,
}: ParseUrlParamsInput): UrlParamsPayload {
  const params = new URLSearchParams(search);

  const newSort: Partial<SortState> = {};
  const newFilters: Filters = {};
  const newTags: string[] = [];
  let perPage: number | undefined;
  let currentPageParam: string | undefined;
  let showColumnsParam: string | undefined;

  const keysToRead = [
    ...Object.keys(filters),
    COLUMNS.tags,
    ...Object.values(URL_PARAMETERS),
  ];

  for (const [key, value] of params) {
    const validatedKey = keysToRead.find(
      (el) => el.toLowerCase() === key.toLowerCase(),
    );

    // unknown key, go next
    if (!validatedKey) continue;

    // remove the wrong-case duplicate from the real URL when the canonical key
    // is found, so the address bar stays normalized
    if (key !== validatedKey) {
      deleteQueryParam(key);
    }

    // filters
    if (validatedKey in filters) {
      newFilters[validatedKey] = value;
      continue;
    }

    switch (validatedKey) {
      // tags
      case COLUMNS.tags:
        newTags.push(...new Set(value.split(',')));
        break;

      // sort
      case URL_PARAMETERS.column: {
        const column = findArrayElementCaseInsensitive(
          value,
          renderableColumns,
        );
        if (column) newSort.column = column;
        break;
      }
      case URL_PARAMETERS.direction: {
        const direction = findArrayElementCaseInsensitive(value, [ASC, DESC]);
        if (direction) newSort.direction = direction;
        break;
      }

      // pagination — currentPage is validated after the loop, once filters are known
      case URL_PARAMETERS.perPage:
        if (PER_PAGE_VALUES.includes(Number(value))) {
          perPage = Number(value);
        }
        break;
      case URL_PARAMETERS.currentPage:
        currentPageParam = value;
        break;

      // showColumns is resolved after the loop, once filters/tags/sort are known
      case URL_PARAMETERS.showColumns:
        showColumnsParam = value;
        break;

      default:
        break;
    }
  }

  // visible columns: the URL override (or the schema defaults) plus any column
  // referenced by an active filter, tag selection or sort, so the deep-linked
  // state is actually visible
  const combinedShowColumns = new Set<string>([
    ...resolveShowColumnsFromUrl(
      showColumnsParam,
      defaultShowColumns,
      renderableColumns,
    ),
    ...Object.keys(newFilters),
  ]);
  if (newTags.length > 0) {
    combinedShowColumns.add(COLUMNS.tags);
  }
  if (newSort.column) {
    combinedShowColumns.add(newSort.column);
  }

  // clamp currentPage against the *filtered* row count (sort doesn't change the
  // count), so a deep link can't land on a page that no longer exists
  let currentPage: number | undefined;
  if (currentPageParam != null) {
    const itemsPerPage = perPage ?? DEFAULT_PER_PAGE;
    const filteredCount = filterTableData(
      [...websites],
      newFilters,
      newTags,
    ).length;
    const newCurrentPage = Number(currentPageParam);
    if (
      newCurrentPage > 0 &&
      newCurrentPage <= Math.ceil(filteredCount / itemsPerPage)
    ) {
      currentPage = newCurrentPage;
    }
  }

  return {
    currentPage,
    perPage,
    filters: newFilters,
    showColumns: Array.from(combinedShowColumns),
    sort: newSort,
    tags: newTags,
  };
}

// Resolve the visible columns named by the `showColumns` URL value, supporting
// the `all` / `none` aliases and an explicit case-insensitive list, falling
// back to the schema defaults.
function resolveShowColumnsFromUrl(
  param: string | undefined,
  defaultShowColumns: string[],
  renderableColumns: string[],
): string[] {
  const normalized = param?.toLowerCase();
  if (!normalized) return defaultShowColumns;
  if (normalized === 'all') return renderableColumns;
  if (normalized === 'none') return [];

  const columns = normalized
    .split(',')
    .map((column) => findArrayElementCaseInsensitive(column, renderableColumns))
    .filter(
      (column): column is string =>
        column != null && renderableColumns.includes(column),
    );
  return columns.length > 0 ? columns : defaultShowColumns;
}

const initialState: TableState = {
  allTags: [],
  checkboxes: [],
  currentPage: DEFAULT_CURRENT_PAGE,
  defaultShowColumns: [],
  filters: {},
  perPage: DEFAULT_PER_PAGE,
  renderableColumns: [],
  showColumns: [],
  sort: { column: '', direction: '' },
  tags: [],
  websitesData: null,
  websitesDataETag: null,
  websitesDataLoaded: false,
  websitesDataSource: '',
  websitesDataStatus: 'idle',
};

const tableSlice = createSlice({
  name: 'table',
  initialState,
  reducers: {
    checkboxToggled(state, action: PayloadAction<number>) {
      const idx = state.checkboxes.indexOf(action.payload);
      if (idx > -1) {
        state.checkboxes.splice(idx, 1);
      } else {
        state.checkboxes.push(action.payload);
      }
    },

    currentPageUpdated(state, action: PayloadAction<number>) {
      state.currentPage = action.payload;
    },

    filtersUpdated(state, action: PayloadAction<Filters>) {
      Object.assign(state.filters, action.payload);
    },

    perPageUpdated(state, action: PayloadAction<number>) {
      state.perPage = action.payload;
    },

    showColumnsUpdated(state, action: PayloadAction<string[]>) {
      state.showColumns = filterAndSortColumns(action.payload, state);
    },

    sortUpdated(state, action: PayloadAction<Partial<SortState>>) {
      Object.assign(state.sort, action.payload);
    },

    tagsUpdated(state, action: PayloadAction<string[]>) {
      state.tags = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getWebsitesData.pending, (state) => {
        state.websitesDataStatus = 'pending';
      })
      .addCase(getWebsitesData.fulfilled, (state, action) => {
        const { urlParams, ...payload } = action.payload;
        state.websitesDataStatus = 'succeeded';
        state.allTags = payload.allTags;
        state.defaultShowColumns = payload.defaultShowColumns;
        state.renderableColumns = payload.renderableColumns;
        state.websitesData = payload.websitesData;
        state.websitesDataETag = payload.websitesDataETag;
        state.websitesDataLoaded = true;
        state.websitesDataSource = payload.websitesDataSource;

        // Hydrate filters/sort/pagination/columns from the URL in the same
        // transition that marks the data loaded, so the first render already
        // reflects a deep link (no flash of default state).
        state.filters = payload.filters;
        Object.assign(state.filters, urlParams.filters);
        if (urlParams.currentPage != null) {
          state.currentPage = urlParams.currentPage;
        }
        if (urlParams.perPage != null) {
          state.perPage = urlParams.perPage;
        }
        state.showColumns = filterAndSortColumns(urlParams.showColumns, state);
        Object.assign(state.sort, urlParams.sort);
        state.tags = urlParams.tags;
      })
      .addCase(getWebsitesData.rejected, (state) => {
        state.websitesDataStatus = 'failed';
      });
  },
});

export const {
  checkboxToggled,
  currentPageUpdated,
  filtersUpdated,
  perPageUpdated,
  showColumnsUpdated,
  sortUpdated,
  tagsUpdated,
} = tableSlice.actions;

export const tableReducer = tableSlice.reducer;
